import { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';

function unwrapList(payload, key) {
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.[key]) ? payload[key] : [];
}

function toTimestamp(value) {
  const timestamp = value ? new Date(value).getTime() : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function normalizeRecent(projects, canvases) {
  const writing = projects.map(project => ({
    id: `writing-${project.id}`,
    sourceId: project.id,
    type: 'writing',
    title: project.title || '未命名剧本',
    stage: project.current_stage || '创作中',
    time: project.updated_at || project.created_at,
    href: `/writing/${project.id}`,
  }));
  const canvasItems = canvases.map(canvas => ({
    id: `canvas-${canvas.id}`,
    sourceId: canvas.id,
    type: 'canvas',
    title: canvas.name || '未命名画布',
    stage: '视觉制作',
    time: canvas.updated_at || canvas.created_at,
    href: `/canvas/${canvas.id}`,
  }));

  return [...writing, ...canvasItems]
    .sort((a, b) => toTimestamp(b.time) - toTimestamp(a.time))
    .slice(0, 6);
}

const PREVIEW_RECENT = [
  {
    id: 'preview-writing',
    sourceId: 'preview-writing',
    type: 'writing',
    title: '门后的两个人',
    stage: '正文创作',
    time: new Date().toISOString(),
    href: '/writing',
  },
  {
    id: 'preview-canvas',
    sourceId: 'preview-canvas',
    type: 'canvas',
    title: '第三集 · 雨夜重逢',
    stage: '视觉制作',
    time: new Date(Date.now() - 3600000).toISOString(),
    href: '/canvas',
  },
  {
    id: 'preview-outline',
    sourceId: 'preview-outline',
    type: 'writing',
    title: '旧城来信',
    stage: '分集大纲',
    time: new Date(Date.now() - 86400000).toISOString(),
    href: '/writing',
  },
];

export default function useHomeData({ preview = false, authenticated = false } = {}) {
  const [state, setState] = useState({
    loading: !preview && authenticated,
    recent: preview ? PREVIEW_RECENT : [],
    failedSources: [],
  });

  const load = useCallback(async () => {
    if (preview) {
      setState({ loading: false, recent: PREVIEW_RECENT, failedSources: [] });
      return;
    }
    if (!authenticated) {
      setState({ loading: false, recent: [], failedSources: [] });
      return;
    }
    setState(previous => ({ ...previous, loading: true, failedSources: [] }));
    const [projectsResult, canvasesResult] = await Promise.allSettled([
      api.get('/agent/projects'),
      api.get('/canvas'),
    ]);

    const projects = projectsResult.status === 'fulfilled'
      ? unwrapList(projectsResult.value.data, 'projects')
      : [];
    const canvases = canvasesResult.status === 'fulfilled'
      ? unwrapList(canvasesResult.value.data, 'canvases')
      : [];
    const failedSources = [
      ...(projectsResult.status === 'rejected' ? ['剧本'] : []),
      ...(canvasesResult.status === 'rejected' ? ['画布'] : []),
    ];

    setState({
      loading: false,
      recent: normalizeRecent(projects, canvases),
      failedSources,
    });
  }, [authenticated, preview]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, retry: load };
}
